import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import client from "@/lib/api";
import { Loader2, Download, ZoomIn, ZoomOut, Maximize2, FileWarning } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// HD, zoomable viewer for an authenticated redacted-plan PDF. Because react-pdf
// re-rasterizes from the vector PDF at the requested width, zooming stays sharp
// (no pixelation) — essential for reading plan dimensions. `src` is an API path.
export default function PlanPdfViewer({ src, filename = "plan.pdf", heightClass = "h-[70vh]" }) {
  const [blob, setBlob] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(false);
  const [baseW, setBaseW] = useState(0);
  const [zoom, setZoom] = useState(1);
  const bodyRef = useRef(null);

  useEffect(() => {
    let active = true;
    setBlob(null); setError(false); setNumPages(0);
    client.get(src, { responseType: "blob" })
      .then((r) => { if (active) setBlob(r.data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [src]);

  const file = useMemo(() => (blob ? { url: URL.createObjectURL(blob) } : null), [blob]);
  useEffect(() => () => { if (file?.url) URL.revokeObjectURL(file.url); }, [file]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setBaseW(Math.min(el.clientWidth - 16, 1400));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [blob]);

  const download = () => {
    if (!blob) return;
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(u), 2000);
  };
  const openTab = () => { if (file?.url) window.open(file.url, "_blank", "noopener"); };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="plan-pdf-viewer">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-slate-50/70 px-2 py-1.5">
        <Button size="icon" variant="outline" className="h-8 w-8" data-testid="pdf-zoom-out" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}><ZoomOut className="h-4 w-4" /></Button>
        <span className="w-12 text-center text-xs font-semibold text-ink" data-testid="pdf-zoom-label">{Math.round(zoom * 100)}%</span>
        <Button size="icon" variant="outline" className="h-8 w-8" data-testid="pdf-zoom-in" onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}><ZoomIn className="h-4 w-4" /></Button>
        <span className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid="pdf-open-tab" onClick={openTab}><Maximize2 className="h-3.5 w-3.5" />Open</Button>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid="pdf-download" onClick={download}><Download className="h-3.5 w-3.5" />Download</Button>
        </span>
      </div>
      <div ref={bodyRef} className={`overflow-auto bg-slate-100 px-2 py-3 ${heightClass}`} onContextMenu={(e) => e.preventDefault()}>
        {error ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground"><FileWarning className="h-6 w-6" />Couldn't display this PDF. Use Download instead.</div>
        ) : !file ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading plan…</div>
        ) : (
          <Document file={file} onLoadSuccess={({ numPages }) => setNumPages(numPages)} onLoadError={() => setError(true)}
            loading={<div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading plan…</div>}>
            {Array.from({ length: numPages }, (_, i) => (
              <Page key={i} pageNumber={i + 1} width={baseW ? baseW * zoom : undefined} renderTextLayer={false} renderAnnotationLayer={false} className="mx-auto mb-3 shadow-sm" />
            ))}
          </Document>
        )}
      </div>
    </div>
  );
}
